import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { deleteCompany, editCompany, getCompanies } from "../../../slices/admin/companySlice";
import { Delete, Pen } from "lucide-react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Switch from "react-switch";
import { customTableStyles } from "../../../utils/tableStylings";

const AdminCompanies = () => {
    const dispatch = useDispatch();
    const { companies, loading, error } = useSelector((state) => state.adminCompanies);
    const [search, setSearch] = useState("");  // State for search input
    const [filteredCompanies, setFilteredCompanies] = useState([]);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const statusFilter = queryParams.get('status'); 

    useEffect(() => {
        dispatch(getCompanies());
    }, [dispatch]);

    useEffect(() => {
        setFilteredCompanies(companies);  // Initialize with full company list
    }, [companies]);

    useEffect(() => {
        // Filter companies based on search term and status (from URL query)
        const results = companies.filter(company => {
            const matchesSearch = company.company_name.toLowerCase().includes(search.toLowerCase()) ||
                company.user_name.toLowerCase().includes(search.toLowerCase()) ||
                company.email.toLowerCase().includes(search.toLowerCase()) ||
                company.mobile_no.toString().includes(search.toLowerCase()) ||
                company.trade.toLowerCase().includes(search.toLowerCase()) ||
                company.company_pan_number.toLowerCase().includes(search.toLowerCase());
    
            // Map status 'pending' to 0 and 'approved' to 1
            const statusMap = {
                'pending': 0,
                'approved': 1
            };
    
            // Get the status value from the query string
            const statusFromQuery = statusFilter ? statusMap[statusFilter.toLowerCase()] : null;
    
            const matchesStatus = statusFromQuery !== null ? company.status === statusFromQuery : true;
    
            return matchesSearch && matchesStatus;
        });
    
        setFilteredCompanies(results);
    }, [search, companies, statusFilter]);
    

    const handleDelete = (id) => {
        Swal.fire({
            title: '<strong>Are you sure?</strong>',
            html: `<p>This action will <b>permanently delete</b> this company.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteCompany(id));
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'The company has been deleted.',
                    toast: true,          // Enable toast style
                    position: 'top-end',  // Position at the top-right corner
                    showConfirmButton: false,  // Hide the confirm button
                    timer: 3000,          // Set the timer to auto-close after 3 seconds
                    timerProgressBar: true,  // Show the progress bar for the timer
                    didOpen: (toast) => {
                        toast.addEventListener('mouseenter', Swal.stopTimer);
                        toast.addEventListener('mouseleave', Swal.resumeTimer);
                    }
                });
                
            }
        });
    };
    
    const handleStatusChange = (id, checked) => {
        // Dispatch an action to update the company status
        dispatch(editCompany({ 
            id: id, 
            formData: { status: checked }  // Correct the formData to be an object, not an array
        }));
    };
    
    const columns = [
        {
            name: "#", 
            selector: (row, index) => index + 1, // Add serial number starting from 1
            sortable: false, // No need to sort by S.No
            width: "50px" // Optional: set a fixed width for the column
        },
        { name: "Company Name", selector: row => row.company_name, sortable: true },
        { name: "User Name", selector: row => row.user_name, sortable: true },
        { name: "Email", selector: row => row.email, sortable: true },
        { name: "Phone", selector: row => row.mobile_no, sortable: true },
        { name: "Trade", selector: row => row.trade, sortable: true },
        { name: "Pan Number", selector: row => row.company_pan_number, sortable: true },
        { name: "Status", 
            cell: row => (
                <Switch
                    checked={row.status === 1}  // Assuming 'active' is true status and 'inactive' is false
                    onChange={(checked) => handleStatusChange(row.id, checked)}
                    offColor="#d6d6d6"
                    onColor="#4caf50"
                    offHandleColor="#888"
                    onHandleColor="#fff"
                    uncheckedIcon={false}
                    checkedIcon={false}
                />
            ),
            sortable: true
        },
        {
            name: "Action",
            cell: row => (
                <>
                    <Link className="text-success me-3" to={`/admin/companies/edit`} state={row}>
                        <Pen />
                    </Link>
                    <button
                        className="text-danger btn btn-link p-0"
                        onClick={() => handleDelete(row.id)}
                    >
                        <Delete />
                    </button>
                </>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            sortable: false
        }
    ];

    return (
        <div className="row">
            <div className="col-12">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="card-title">Companies</h4>
                        <Link to={'/admin/companies/add'} className="btn btn-primary">Add New</Link>
                    </div>
                    <div className="card-body">
                        <input
                            type="text"
                            placeholder="Search Companies"
                            className="form-control mb-3"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <div className="table-responsive">
                            <DataTable
                                className="table-hover custom-table"
                                columns={columns}
                                data={filteredCompanies} // Use filtered data
                                pagination
                                striped
                                progressPending={loading}
                                customStyles={customTableStyles}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminCompanies;
